<template>
  <div v-if="loading" class="skeleton-loader-wrapper">
    <SkeletonLoader />
  </div>
  <div v-else>
    <MainHeader title="Categories">
      <template #right>
        <div
          class="help-beacon"
          @click="openBeacon"
          title="Get Help"
          role="button"
          aria-label="Get Help"
        >
          <font-awesome-icon
            :icon="['fas', 'question']"
            class="help-beacon-icon"
          />
        </div>
      </template>
    </MainHeader>

    <div class="view categories-view">
      <div class="flex-med-buttons mb-2">
        <button
          class="teal-button w-100 mb-1 mb-med-none"
          @click="router.push({ name: 'AddCategory' })"
          aria-label="Add Category"
        >
          + Add Category
        </button>

        <div class="search-row w-100">
          <input
            type="text"
            class="search-input"
            v-model="searchText"
            placeholder="Search categories..."
            aria-label="Search Categories"
          />
          <div class="search-icon-wrap">
            <font-awesome-icon
              v-if="searchText && searchText.length > 0"
              :icon="['fas', 'circle-xmark']"
              class="search-icon pointer"
              @click="clearSearchText"
            />
            <font-awesome-icon
              v-else
              :icon="['fas', 'magnifying-glass-dollar']"
              class="search-icon"
            />
          </div>
        </div>
      </div>

      <div
        v-if="missingBudgetCount > 0 && !searchText"
        class="budget-warning-banner mb-2"
      >
        <div class="warning-icon-wrap">
          <font-awesome-icon :icon="['fas', 'triangle-exclamation']" />
        </div>
        <div class="warning-content">
          <div class="warning-title">
            <strong>{{ missingBudgetCount }}</strong>
            {{
              missingBudgetCount === 1 ? 'category needs' : 'categories need'
            }}
            a default budget
          </div>
          <div class="warning-sub">
            Default budgets auto-fill each new month. Without one, you'll set
            the amount manually every month.
          </div>
        </div>
        <button
          class="warning-toggle-btn"
          @click="showOnlyMissingBudgets = !showOnlyMissingBudgets"
        >
          {{ showOnlyMissingBudgets ? 'Show all' : 'Review' }}
        </button>
      </div>

      <EmptyState
        v-if="!filteredCategories || filteredCategories.length === 0"
        :title="
          showOnlyMissingBudgets
            ? 'All set'
            : searchText
              ? 'No matches'
              : 'No categories yet'
        "
        :subtitle="
          showOnlyMissingBudgets
            ? 'Every category has a default budget. Nice work.'
            : searchText
              ? 'Try a different search term.'
              : 'Add your first category to start organising your transactions.'
        "
      >
        <!-- <EmptyState
        v-if="!filteredCategories || filteredCategories.length === 0"
        title="No categories yet"
        subtitle="Add your first category to start organising your transactions."
      > -->
        <template #icon>
          <svg viewBox="0 0 72 72" fill="none">
            <rect x="4" y="4" width="64" height="64" rx="16" fill="#EEEDFE" />
            <path
              d="M24 28h24M24 36h16M24 44h20"
              stroke="#7F77DD"
              stroke-width="2.5"
              stroke-linecap="round"
            />
            <circle cx="52" cy="20" r="9" fill="#5DCAA5" />
            <path
              d="M48.5 20l2.5 2.5 4-4"
              stroke="white"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </template>
        <template #actions>
          <button
            v-if="!showOnlyMissingBudgets && !searchText"
            class="gradient-button w-100"
            @click="router.push({ name: 'AddCategory' })"
          >
            + Add my first category
          </button>
          <button
            v-else-if="showOnlyMissingBudgets"
            class="outline-button w-100"
            @click="showOnlyMissingBudgets = false"
          >
            Show all categories
          </button>
        </template>
        <!-- <template #actions>
          <button
            class="gradient-button w-100"
            @click="router.push({ name: 'AddCategory' })"
          >
            + Add my first category
          </button>
        </template> -->
      </EmptyState>

      <div v-else>
        <div
          v-for="(item, index) in filteredCategories"
          :key="item.categoryID"
          class="cat-card mb-1"
        >
          <div class="cat-row">
            <div
              class="cat-icon-wrap"
              @click="editCategory(item, false)"
              role="button"
              :aria-label="item.name"
            >
              <span class="icon-wrapper">
                <font-awesome-icon
                  :icon="
                    item.icon ? parseIconString(item.icon) : ['fas', 'tag']
                  "
                  :style="{ color: item.iconColor }"
                  class="cat-icon"
                  aria-label="category icon"
                />
              </span>
            </div>
            <div
              class="cat-name-wrap"
              @click="editCategory(item, false)"
              aria-label="category name"
            >
              <span class="cat-name">{{ item.name }}</span>
              <span
                v-if="isCategoryMissingBudget(item)"
                class="missing-budget-pill"
                title="No default budget set"
              >
                <font-awesome-icon :icon="['fas', 'triangle-exclamation']" />
                No budget
              </span>
            </div>
            <!-- <div
              class="cat-name"
              @click="editCategory(item, false)"
              aria-label="category name"
            >
              {{ item.name }}
            </div> -->
            <div class="cat-right">
              <span
                v-if="item.subcategories && item.subcategories.length > 0"
                class="sub-count"
              >
                {{ item.subcategories.length }}
              </span>
              <div
                v-if="item.subcategories && item.subcategories.length > 0"
                class="expand-btn"
                :class="{ expanded: showItemSubcategories[index] }"
                @click="
                  showItemSubcategories[index] = !showItemSubcategories[index]
                "
                aria-label="expand subcategories"
              >
                <font-awesome-icon :icon="['fas', 'caret-down']" />
              </div>
            </div>
          </div>

          <transition name="sub-slide">
            <div
              v-if="
                item.subcategories &&
                item.subcategories.length > 0 &&
                showItemSubcategories[index]
              "
              class="sub-list"
            >
              <div
                v-for="subcategory in item.subcategories"
                :key="subcategory.subcategoryID"
                class="sub-row"
                @click="editCategory(subcategory, true)"
              >
                <span class="sub-icon-wrapper">
                  <font-awesome-icon
                    aria-label="subcategory icon"
                    :icon="
                      subcategory.icon
                        ? parseIconString(subcategory.icon)
                        : ['fas', 'tag']
                    "
                    :style="{ color: subcategory.iconColor }"
                    class="cat-icon"
                  />
                </span>
                <span class="sub-name-wrap">
                  <span class="sub-name">{{ subcategory.name }}</span>
                  <span
                    v-if="isSubcategoryMissingBudget(subcategory)"
                    class="missing-budget-pill sub-pill"
                    title="No default budget set"
                  >
                    <font-awesome-icon
                      :icon="['fas', 'triangle-exclamation']"
                    />
                  </span>
                </span>
                <!-- <span class="sub-name">{{ subcategory.name }}</span> -->
                <font-awesome-icon
                  :icon="['fas', 'chevron-right']"
                  class="sub-arrow"
                />
              </div>
            </div>
          </transition>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { useMyStore } from '../store';
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useRouter } from 'vue-router';
import MainHeader from '../components/View-Header.vue';
import SkeletonLoader from '../components/SkeletonLoader.vue';
import EmptyState from '../components/EmptyState.vue';

export default {
  name: 'UserCategories',
  components: { MainHeader, SkeletonLoader, EmptyState },
  setup() {
    const store = useMyStore();
    const showItemSubcategories = ref({});
    const router = useRouter();
    const loading = ref(true);
    // let searchText = ref('');
    const searchText = ref('');
    const showOnlyMissingBudgets = ref(false);
    // let filteredCategories = ref([]);

    const hasDefaultBudget = (item) =>
      item && typeof item.defaultBudget === 'number' && item.defaultBudget > 0;

    const isCategoryMissingBudget = (category) => {
      if (!category) return false;
      if (!hasDefaultBudget(category)) return true;
      if (category.subcategories && category.subcategories.length > 0) {
        return category.subcategories.some((sub) => !hasDefaultBudget(sub));
      }
      return false;
    };

    // const isCategoryMissingBudget = (category) => {
    //   if (!category) return false;
    //   const hasSubs =
    //     category.subcategories && category.subcategories.length > 0;
    //   if (!hasSubs) return !hasDefaultBudget(category);
    //   const anySubHasBudget = category.subcategories.some(hasDefaultBudget);
    //   return !hasDefaultBudget(category) && !anySubHasBudget;
    // };

    const isSubcategoryMissingBudget = (subcategory) =>
      !hasDefaultBudget(subcategory);

    const missingBudgetCount = computed(
      () => store.categories.filter(isCategoryMissingBudget).length
    );

    const filteredCategories = computed(() => {
      const searchTerm = searchText.value.toLowerCase().trim();

      return store.categories.reduce((acc, category) => {
        if (
          showOnlyMissingBudgets.value &&
          !isCategoryMissingBudget(category)
        ) {
          return acc;
        }

        if (searchTerm) {
          const categoryMatch = category.name
            .toLowerCase()
            .includes(searchTerm);
          const matchingSubs = category.subcategories
            ? category.subcategories.filter((sub) =>
                sub.name.toLowerCase().includes(searchTerm)
              )
            : [];

          if (!categoryMatch && matchingSubs.length === 0) return acc;

          acc.push({
            ...category,
            subcategories: categoryMatch
              ? category.subcategories
              : matchingSubs,
          });
        } else {
          acc.push(category);
        }

        return acc;
      }, []);
    });

    const editCategory = async (category, isSubcategory) => {
      store.updateEditCategory(category, isSubcategory);

      const params = { categoryID: category.categoryID };
      if (isSubcategory && Number.isInteger(category.subcategoryID)) {
        params.subcategoryID = category.subcategoryID;
      }

      try {
        await store.getCategoryRules(params);
      } catch (e) {
        console.warn('getCategoryRules failed', e);
      }

      router.push({ name: 'EditCategory' });
    };

    const parseIconString = (iconString) => {
      if (iconString) {
        const [category, icon] = iconString.split(' ');
        return [category, icon];
      }
      return null;
    };

    // const clearSearchText = () => {
    //   searchText.value = '';
    //   filteredCategories.value = store.categories;
    //   searchCategories('');
    // };

    const clearSearchText = () => {
      searchText.value = '';
    };

    // const refreshCategories = async () => {
    //   loading.value = true;
    //   await store.getAllCategories();
    //   filteredCategories.value = store.categories;
    //   loading.value = false;
    // };

    const refreshCategories = async () => {
      loading.value = true;
      await store.getAllCategories();
      loading.value = false;
    };

    // const searchCategories = () => {
    //   const searchTerm = searchText.value.toLowerCase();
    //   filteredCategories.value = store.categories.filter((category) => {
    //     const categoryMatch = category.name.toLowerCase().includes(searchTerm);
    //     const matchingSubcategories = category.subcategories
    //       ? category.subcategories.filter((subcategory) =>
    //           subcategory.name.toLowerCase().includes(searchTerm)
    //         )
    //       : [];
    //     if (categoryMatch || matchingSubcategories.length > 0) {
    //       return { ...category, subcategories: matchingSubcategories };
    //     }
    //     return false;
    //   });
    // };

    onMounted(async () => {
      setTimeout(() => {
        const mainElement = document.querySelector('.main');
        if (mainElement) mainElement.scrollTo(0, 0);
      }, 100);

      await refreshCategories();
    });

    // watch(searchText, () => {
    //   searchCategories();
    // });

    // onMounted(() => {
    //   setTimeout(() => {
    //     const mainElement = document.querySelector('.main');
    //     if (mainElement) mainElement.scrollTo(0, 0);
    //   }, 100);

    //   store.getAllCategories().then(() => {
    //     loading.value = false;
    //     filteredCategories.value = store.categories;
    //   });
    // });

    onBeforeUnmount(() => {
      window.Beacon('close');
    });

    const openBeacon = () => {
      if (window.Beacon && typeof window.Beacon === 'function') {
        window.Beacon('on', 'ready', () => {
          window.Beacon('open');
          window.Beacon('navigate', '/docs/search?query=categories');
        });
        window.Beacon('open');
        window.Beacon('navigate', '/docs/search?query=categories');
      } else {
        console.error('Beacon is not initialized');
      }
    };

    return {
      store,
      router,
      showItemSubcategories,
      editCategory,
      parseIconString,
      loading,
      searchText,
      clearSearchText,
      // searchCategories,
      filteredCategories,
      openBeacon,
      missingBudgetCount,
      showOnlyMissingBudgets,
      isCategoryMissingBudget,
      isSubcategoryMissingBudget,
    };
  },
};
</script>

<style scoped>
.help-beacon {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: var(--gradient-primary);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: var(--shadow-purple);
  transition:
    transform var(--transition-fast),
    box-shadow var(--transition-fast);
}
.help-beacon:hover {
  transform: translateY(-1px);
  box-shadow: var(--shadow-purple-lg);
}
.help-beacon-icon {
  color: white;
  font-size: 13px;
}

.search-row {
  display: flex;
  border-radius: var(--radius-pill);
  overflow: hidden;
  box-shadow: var(--shadow-sm);
  border: 1.5px solid #e7e7e7;
  transition:
    border-color var(--transition-fast),
    box-shadow var(--transition-fast);
}
.search-row:focus-within {
  border-color: var(--color-accent-purple);
  box-shadow: 0 0 0 3px rgba(127, 119, 221, 0.1);
}
.dark-theme .search-row {
  border-color: #2a3a4e;
  background: #171e29;
}

.search-input {
  flex: 1;
  border: none;
  background: white;
  padding: 0.6rem 1rem;
  font-family: 'Poppins', sans-serif;
  font-size: 0.88rem;
  color: #213547;
  outline: none;
  border-radius: var(--radius-pill) 0 0 var(--radius-pill);
}
.dark-theme .search-input {
  background: #171e29;
  color: #d4d2d2;
}
.search-input::placeholder {
  color: #aaa;
}

.search-icon-wrap {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 1rem;
  background: white;
  border-radius: 0 var(--radius-pill) var(--radius-pill) 0;
}
.dark-theme .search-icon-wrap {
  background: #171e29;
}
.search-icon {
  color: #888;
  font-size: 1.1rem;
  cursor: pointer;
}

.cat-card {
  background: white;
  border-radius: var(--radius-lg);
  border: 1px solid #f0f0f0;
  box-shadow: var(--shadow-sm);
  overflow: hidden;
  transition: box-shadow var(--transition-base);
  animation: cardSlideUp 0.35s cubic-bezier(0.34, 1.2, 0.64, 1) both;
}
.dark-theme .cat-card {
  background: #212e3e;
  border-color: #2a3a4e;
}
.cat-card:hover {
  box-shadow: var(--shadow-md);
}

.cat-row {
  display: grid;
  grid-template-columns: 44px 1fr auto;
  align-items: center;
  padding: 0.85rem 1rem;
  cursor: pointer;
  gap: 10px;
}

.cat-icon-wrap {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f8f8f8;
  flex-shrink: 0;
}
.dark-theme .cat-icon-wrap {
  background: #1a2535;
}

.cat-icon {
  font-size: 15px;
}

.cat-name {
  font-size: 0.9rem;
  font-weight: 600;
  color: #213547;
}
.dark-theme .cat-name {
  color: white;
}

.cat-right {
  display: flex;
  align-items: center;
  gap: 8px;
}

.sub-count {
  font-size: 10px;
  font-weight: 700;
  color: var(--color-accent-purple);
  background: var(--purple-50);
  padding: 0 8px;
  height: 20px;
  border-radius: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding-top: 1px;
}
.dark-theme .sub-count {
  background: #1e1a40;
}

.expand-btn {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #888;
  transition:
    transform 0.25s ease,
    background 0.15s;
  cursor: pointer;
}
.expand-btn:hover {
  background: #f0f0f0;
}
.dark-theme .expand-btn:hover {
  background: #2a3a4e;
}
.expand-btn.expanded {
  transform: rotate(180deg);
  color: var(--color-accent-purple);
}

.sub-list {
  border-top: 1px solid #f0f0f0;
  padding: 0.25rem 0;
}
.dark-theme .sub-list {
  border-top-color: #2a3a4e;
}

.sub-row {
  display: grid;
  grid-template-columns: 44px 1fr auto;
  align-items: center;
  padding: 0.65rem 1rem 0.65rem 1.25rem;
  cursor: pointer;
  gap: 10px;
  transition: background var(--transition-fast);
}
.sub-row:hover {
  background: #fafafa;
}
.dark-theme .sub-row:hover {
  background: #1a2535;
}

.sub-icon-wrapper {
  width: 30px;
  height: 30px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f4f4f8;
  flex-shrink: 0;
}
.dark-theme .sub-icon-wrapper {
  background: #212e3e;
}

.sub-name {
  font-size: 0.82rem;
  font-weight: 500;
  color: #515a65;
}
.dark-theme .sub-name {
  color: #d4d2d2;
}

.sub-arrow {
  font-size: 0.7rem;
  color: #ccc;
}
.dark-theme .sub-arrow {
  color: #4a5a6e;
}

.sub-slide-enter-from,
.sub-slide-leave-to {
  transform: translateY(-4px);
}
.sub-slide-enter-to,
.sub-slide-leave-from {
  opacity: 1;
  max-height: 600px;
  transform: translateY(0);
}

.icon-wrapper svg {
  display: block;
  position: relative;
}

.budget-warning-banner {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 0.85rem 1rem;
  border-radius: var(--radius-md);
  background: rgba(216, 90, 48, 0.07);
  border-left: 4px solid #d85a30;
  animation: cardSlideUp 0.35s cubic-bezier(0.34, 1.2, 0.64, 1) both;
}
.dark-theme .budget-warning-banner {
  background: rgba(216, 90, 48, 0.12);
}

.warning-icon-wrap {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: rgba(216, 90, 48, 0.15);
  color: #d85a30;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-size: 0.9rem;
}

.warning-content {
  flex: 1;
  min-width: 0;
}
.warning-title {
  font-size: 0.85rem;
  color: #213547;
  margin-bottom: 2px;
}
.dark-theme .warning-title {
  color: #d4d2d2;
}
.warning-sub {
  font-size: 0.72rem;
  color: #888;
  line-height: 1.4;
}

.warning-toggle-btn {
  background: #d85a30;
  border: none;
  color: white;
  padding: 0.45rem 1rem;
  border-radius: var(--radius-pill);
  font-size: 0.78rem;
  font-weight: 600;
  font-family: 'Poppins', sans-serif;
  cursor: pointer;
  white-space: nowrap;
  flex-shrink: 0;
  transition: opacity var(--transition-fast);
}
.warning-toggle-btn:hover {
  opacity: 0.88;
}

.cat-name-wrap {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  flex-wrap: wrap;
}

.sub-name-wrap {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
}

.missing-budget-pill {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 9px;
  font-weight: 600;
  color: #b8895f;
  background: rgba(216, 156, 110, 0.12);
  padding: 0 7px;
  height: 18px;
  border-radius: 20px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  white-space: nowrap;
  flex-shrink: 0;
}
.missing-budget-pill svg {
  font-size: 8px;
}
.missing-budget-pill.sub-pill {
  padding: 0 5px;
  height: 16px;
}
.missing-budget-pill.sub-pill svg {
  font-size: 7px;
}
.dark-theme .missing-budget-pill {
  background: rgba(216, 156, 110, 0.18);
  color: #d8a87e;
}

@media screen and (min-width: 992px) {
  .icon-wrapper svg {
    line-height: 50px;
  }
}
</style>

<!-- <template>
  <div v-if="loading" class="skeleton-loader-wrapper">
    <SkeletonLoader />
  </div>
  <div v-else>
    <div>
      <MainHeader title="Categories">
        <template #right>
          <div class="beacon-launcher" @click="openBeacon" title="Get Help">
            <font-awesome-icon
              :icon="['fas', 'question']"
              class="beacon-icon"
            />
          </div>
        </template>
      </MainHeader>
    </div>
    <div class="view categories-view">
      <div class="flex-med-buttons">
        <button
          class="blue-button mb-2 mb-lg-7 w-100"
          @click="router.push({ name: 'AddCategory' })"
          aria-label="Add Category"
        >
          Add Category
        </button>

        <div class="mb-2 mb-lg-7 w-100 flex">
          <input
            type="text"
            class="input"
            v-model="searchText"
            placeholder="Search Categories"
            @keydown.enter="searchCategories(searchText)"
            aria-label="Search Categories"
          />
          <div class="search-icon-wrapper">
            <font-awesome-icon
              v-if="searchText && searchText.length > 0"
              :icon="['fas', 'circle-xmark']"
              class="search-icon pointer"
              @click="clearSearchText"
            />
            <font-awesome-icon
              v-else
              :icon="['fas', 'magnifying-glass-dollar']"
              class="search-icon pointer"
            />
          </div>
        </div>
      </div>
      <div v-for="(item, index) in filteredCategories" :key="item" class="mb-2">
        <div class="category-list align-center pointer">
          <div class="icon" @click="editCategory(item, false)">
            <span class="icon-wrapper">
              <font-awesome-icon
                :icon="item.icon ? parseIconString(item.icon) : ['fas', 'tag']"
                :style="{ color: item.iconColor }"
                class="cat-icon"
                aria-label="category icon"
              />
            </span>
          </div>
          <div
            class="category-label"
            @click="editCategory(item, false)"
            aria-label="category name"
          >
            {{ item.name }}
          </div>
          <div
            class="text-right"
            v-if="item.subcategories && item.subcategories.length > 0"
            @click="
              showItemSubcategories[index] = !showItemSubcategories[index]
            "
            aria-label="expand subcategories"
          >
            <font-awesome-icon
              :icon="['fas', 'caret-down']"
              aria-label="expand subcategories icon"
            />
          </div>
          <div
            v-if="item.subcategories && item.subcategories.length > 0"
            class="subcategory-area"
          >
            <div v-if="showItemSubcategories[index]">
              <div v-for="subcategory in item.subcategories" :key="subcategory">
                <div
                  class="subcategory-list align-center"
                  @click="editCategory(subcategory, true)"
                >
                  <div class="icon">
                    <span class="icon-wrapper">
                      <font-awesome-icon
                        aria-label="subcategory icon"
                        :icon="
                          subcategory.icon
                            ? parseIconString(subcategory.icon)
                            : ['fas', 'tag']
                        "
                        :style="{ color: subcategory.iconColor }"
                        class="cat-icon"
                      />
                    </span>
                  </div>
                  <div aria-label="subcategory name">
                    {{ subcategory.name }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { useMyStore } from '../store';
import { ref, onMounted, onBeforeUnmount } from 'vue';
import { useRouter } from 'vue-router';

import MainHeader from '../components/View-Header.vue';
import SkeletonLoader from '../components/SkeletonLoader.vue';

export default {
  name: 'User-Categories',
  components: { MainHeader, SkeletonLoader },
  setup() {
    const store = useMyStore();
    const showItemSubcategories = ref({});
    const router = useRouter();
    const loading = ref(true);
    let searchText = ref('');
    let filteredCategories = ref([]);

    const editCategory = async (category, isSubcategory) => {
      store.updateEditCategory(category, isSubcategory);

      const params = { categoryID: category.categoryID };
      if (isSubcategory && Number.isInteger(category.subcategoryID)) {
        params.subcategoryID = category.subcategoryID;
      }

      try {
        await store.getCategoryRules(params);
      } catch (e) {
        console.warn('getCategoryRules failed', e);
      }

      router.push({ name: 'EditCategory' });
    };

    const parseIconString = (iconString) => {
      if (iconString) {
        const [category, icon] = iconString.split(' ');
        return [category, icon];
      }
      return null;
    };

    const clearSearchText = () => {
      searchText.value = '';
      filteredCategories.value = store.categories;
      searchCategories('');
    };

    const searchCategories = () => {
      const searchTerm = searchText.value.toLowerCase();
      filteredCategories.value = store.categories.filter((category) => {
        const categoryMatch = category.name.toLowerCase().includes(searchTerm);
        const matchingSubcategories = category.subcategories
          ? category.subcategories.filter((subcategory) =>
              subcategory.name.toLowerCase().includes(searchTerm)
            )
          : [];
        if (categoryMatch || matchingSubcategories.length > 0) {
          return {
            ...category,
            subcategories: matchingSubcategories,
          };
        }
        return false;
      });
    };

    onMounted(() => {
      setTimeout(() => {
        const mainElement = document.querySelector('.main');
        if (mainElement) {
          mainElement.scrollTo(0, 0);
        }
      }, 100);

      store.getAllCategories().then(() => {
        loading.value = false;
        filteredCategories.value = store.categories;
      });
    });

    onBeforeUnmount(() => {
      window.Beacon('close');
    });

    const openBeacon = () => {
      if (window.Beacon && typeof window.Beacon === 'function') {
        window.Beacon('on', 'ready', () => {
          window.Beacon('open');
          window.Beacon('navigate', '/docs/search?query=categories');
        });
        window.Beacon('open');
        window.Beacon('navigate', '/docs/search?query=categories');
      } else {
        console.error('Beacon is not initialized');
      }
    };

    return {
      store,
      router,
      showItemSubcategories,
      editCategory,
      parseIconString,
      loading,
      searchText,
      clearSearchText,
      searchCategories,
      filteredCategories,
      openBeacon,
    };
  },
};
</script>

<style scoped>
.icon-wrapper svg {
  display: block;
  position: relative;
  text-align: center;
  line-height: 30px;
}

.category-label {
  font-weight: 500;
  font-size: 1em;
}

.category-list .fa-arrow-right-long,
.subcategory-list .fa-arrow-right-long {
  color: #d8d8d8;
}

.categories-view .input {
  width: 100%;
  font-size: 1em;
  box-sizing: border-box;
  background-color: #f2f2f2;
  color: #3a3d41;
  opacity: 1;
  -webkit-text-fill-color: #3a3d41;
  -webkit-opacity: 1;
  -webkit-background-color: #f2f2f2;
  padding: 0.6em 1.2em;
  border-bottom-left-radius: 8px;
  border-top-left-radius: 8px;
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
  border: 1px solid transparent;
  font-weight: 500;
  font-family: inherit;
  text-align: center;
  line-height: 1;
}

.light-theme .categories-view .input {
  background-color: #f2f2f2;
  color: #3a3d41;
  -webkit-text-fill-color: #3a3d41;
  -webkit-background-color: #f2f2f2;
}

.light-theme .categories-view .input::placeholder {
  color: #3a3d41;
}

.dark-theme .categories-view .input {
  background: #d4d2d2;
  color: #3a3d41;
  -webkit-text-fill-color: #3a3d41;
  -webkit-background-color: #d4d2d2;
}

.dark-theme .categories-view .input::placeholder {
  color: #3a3d41;
}

.search-icon-wrapper {
  font-size: 1em;
  box-sizing: border-box;
  background-color: #f2f2f2;
  color: #6e747a;
  opacity: 1;
  -webkit-text-fill-color: #6e747a;
  -webkit-opacity: 1;
  -webkit-background-color: #f2f2f2;
  padding: 0.6em 1.2em;
  border-bottom-left-radius: 0;
  border-top-left-radius: 0;
  border-top-right-radius: 8px;
  border-bottom-right-radius: 8px;
  border: 1px solid transparent;
  font-weight: 500;
  font-family: inherit;
  text-align: center;
  line-height: 1;
  display: flex;
  justify-content: center;
  align-items: center;
}

.light-theme .search-icon-wrapper {
  background-color: #f2f2f2;
  color: #6e747a;
  -webkit-text-fill-color: #6e747a;
  -webkit-background-color: #f2f2f2;
}
.dark-theme .search-icon-wrapper {
  background-color: #d4d2d2;
  color: #6e747a;
  -webkit-text-fill-color: #6e747a;
  -webkit-background-color: #d4d2d2;
}

.search-icon {
  color: #6e747a;
  font-size: 1.3em;
  cursor: pointer;
}

@media screen and (min-width: 992px) {
  .icon-wrapper svg {
    line-height: 50px;
  }
}
</style> -->
